import { RootState } from 'store/reducer';
import {
  UiMetadata,
  FeatureEnablementFlagEnum,
  ContactInformationDisplayFlagEnum,
  ContactInformationRequirementFlagEnum,
} from 'store/ui-metadata/types';
import { createSelector } from 'reselect';

export const uiMetadataState = (state: RootState): RootState['uiMetadata'] => state.uiMetadata;
export const uiMetadata = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): UiMetadata | null => state.data,
);

export const uiMetadataLoadingState = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state.loading,
);

export const isCreateVisitsEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.create_visits_enabled ?? false,
);

const defaultTrueIfNewUi = (
  state: RootState['uiMetadata'],
  key:
    | 'search_enabled'
    | 'host_filter_enabled'
    | 'company_filter_enabled'
    | 'status_filter_enabled'
    | 'supports_visit_filtering',
): boolean => {
  const uiMetadataData = state?.data?.ui_metadata;

  if (uiMetadataData && uiMetadataData[key] !== undefined) {
    return Boolean(uiMetadataData[key]);
  }

  if (uiMetadataData?.visitor_management_new_ui) {
    return true;
  }

  return false;
};

export const isSearchEnabledSelector = createSelector(uiMetadataState, (state: RootState['uiMetadata']): boolean =>
  defaultTrueIfNewUi(state, 'search_enabled'),
);

export const isHostFilterEnabledSelector = createSelector(uiMetadataState, (state: RootState['uiMetadata']): boolean =>
  defaultTrueIfNewUi(state, 'host_filter_enabled'),
);

export const isCompanyFilterEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => defaultTrueIfNewUi(state, 'company_filter_enabled'),
);

export const isStatusFilterEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => defaultTrueIfNewUi(state, 'status_filter_enabled'),
);

export const isVisitFilteringSupportedSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => defaultTrueIfNewUi(state, 'supports_visit_filtering'),
);

/**
 * Helper function to map display and required flags to FeatureEnablementFlagEnum
 */
const mapToEnablementFlag = (display: boolean, required: boolean): FeatureEnablementFlagEnum => {
  if (display && required) {
    return FeatureEnablementFlagEnum.ENABLED_REQUIRED;
  }
  if (display) {
    return FeatureEnablementFlagEnum.ENABLED_OPTIONAL;
  }
  return FeatureEnablementFlagEnum.DISABLED;
};

/**
 * Get email field configuration based on contact information settings
 * Skips EMAIL_OR_PHONE case for simplicity
 */
const getEmailConfig = (
  display?: ContactInformationDisplayFlagEnum,
  requirement?: ContactInformationRequirementFlagEnum,
): FeatureEnablementFlagEnum => {
  if (display && requirement) {
    // Skip EMAIL_OR_PHONE case - treat as ENABLED_OPTIONAL
    const required =
      requirement === ContactInformationRequirementFlagEnum.EMAIL ||
      requirement === ContactInformationRequirementFlagEnum.EMAIL_AND_PHONE;

    const shouldDisplay = [
      ContactInformationDisplayFlagEnum.EMAIL,
      ContactInformationDisplayFlagEnum.EMAIL_PHONE,
    ].includes(display);

    return mapToEnablementFlag(shouldDisplay, required);
  }
  return FeatureEnablementFlagEnum.ENABLED_REQUIRED;
};

/**
 * Get phone field configuration based on contact information settings
 * Skips EMAIL_OR_PHONE case for simplicity
 */
const getPhoneConfig = (
  display?: ContactInformationDisplayFlagEnum,
  requirement?: ContactInformationRequirementFlagEnum,
): FeatureEnablementFlagEnum => {
  if (display && requirement) {
    // Skip EMAIL_OR_PHONE case - treat as ENABLED_OPTIONAL
    const required =
      requirement === ContactInformationRequirementFlagEnum.PHONE ||
      requirement === ContactInformationRequirementFlagEnum.EMAIL_AND_PHONE;

    const shouldDisplay = [
      ContactInformationDisplayFlagEnum.PHONE,
      ContactInformationDisplayFlagEnum.EMAIL_PHONE,
    ].includes(display);

    return mapToEnablementFlag(shouldDisplay, required);
  }
  return FeatureEnablementFlagEnum.ENABLED_OPTIONAL;
};

/**
 * Selector for visit_reason field configuration
 */
export const visitReasonConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.visit_reason ?? FeatureEnablementFlagEnum.ENABLED_OPTIONAL,
);

/**
 * Selector for reason_for_visit field configuration
 */
export const reasonForVisitConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.reason_for_visit ?? FeatureEnablementFlagEnum.DISABLED,
);

/**
 * Selector for floor field configuration
 */
export const floorConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.floor ?? FeatureEnablementFlagEnum.ENABLED_OPTIONAL,
);

/**
 * Selector for visitor_company field configuration
 */
export const visitorCompanyConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.visitor_company ?? FeatureEnablementFlagEnum.ENABLED_REQUIRED,
);

/**
 * Selector for acknowledgment_text value
 */
export const acknowledgmentTextSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): string | undefined => state?.data?.ui_metadata?.acknowledgment_text ?? undefined,
);

/**
 * Selector for checkin_instructions field configuration
 */
export const checkinInstructionsConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.checkin_instructions ?? FeatureEnablementFlagEnum.DISABLED,
);

/**
 * Selector for contact information (email and phone) configuration
 * Returns separate configurations for email and phone fields
 */
export const contactInformationConfigSelector = createSelector(
  uiMetadataState,
  (
    state: RootState['uiMetadata'],
  ): {
    email: FeatureEnablementFlagEnum;
    phone: FeatureEnablementFlagEnum;
  } => {
    const display = state?.data?.ui_metadata?.contact_information_display;
    const requirement = state?.data?.ui_metadata?.contact_information_requirement;

    return {
      email: getEmailConfig(display, requirement),
      phone: getPhoneConfig(display, requirement),
    };
  },
);

/**
 * Selector for additional_notification_recipients field configuration
 */
export const additionalNotificationRecipientsConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.additional_notification_recipients ?? FeatureEnablementFlagEnum.ENABLED_OPTIONAL,
);

/**
 * Selector for notes_to_reception field configuration
 */
export const noteForReceptionConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.notes_to_reception ?? FeatureEnablementFlagEnum.DISABLED,
);

/**
 * Selector for notification selection configuration.
 * Reads the top-level email and sms fields from ui_metadata.
 */
export const notificationSelectionConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): { email: FeatureEnablementFlagEnum; sms: FeatureEnablementFlagEnum } => ({
    email: state?.data?.ui_metadata?.email ?? FeatureEnablementFlagEnum.DISABLED,
    sms: state?.data?.ui_metadata?.sms ?? FeatureEnablementFlagEnum.DISABLED,
  }),
);

/**
 * Selector for visitors_typeahead_supported flag
 * Returns true if the adapter supports visitor typeahead search
 */
export const isVisitorsTypeaheadSupportedSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.visitors_typeahead_supported ?? false,
);

/**
 * Selector for visitor_photo field configuration
 */
export const visitorPhotoConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.visitor_photo ?? FeatureEnablementFlagEnum.DISABLED,
);

/**
 * Selector for pass_entry_option_enabled flag
 */
export const passEntryOptionEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.pass_entry_option_enabled ?? false,
);

/**
 * Selector for alt_hosts_enabled flag
 */
export const alternativeHostsEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.alt_hosts_enabled ?? false,
);

/**
 * Selector for recurrence flag
 */
export const recurrenceEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.recurrence ?? false,
);

/**
 * Selector for host_invitation_copy_enabled flag
 */
export const hostInvitationCopyEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.host_invitation_copy_enabled ?? false,
);

/**
 * Selector for host_checkin_notification_enabled flag
 */
export const hostCheckinNotificationEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.host_checkin_notification_enabled ?? false,
);

/**
 * Selector for visit_duration field configuration
 */
export const visitDurationConfigSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): FeatureEnablementFlagEnum =>
    state?.data?.ui_metadata?.visit_duration ?? FeatureEnablementFlagEnum.DISABLED,
);

export const isMultiDayVisitsMetadataEnabledSelector = createSelector(
  uiMetadataState,
  (state: RootState['uiMetadata']): boolean => state?.data?.ui_metadata?.enable_multi_day_visits === true,
);
